import React, { useEffect, useState } from "react";
import axios from "axios";
import config from "../../config";
import dayjs from "dayjs";
import moment from "moment";
import { ArrowTrendingDownIcon } from "@heroicons/react/24/outline";
import {
  Table,
  Button,
  DatePicker,
  Form,
  Select,
  Row,
  Col,
  Tooltip,
  message,
} from "antd";
import { NavLink } from "react-router-dom";
import { PrinterOutlined, EditOutlined } from "@ant-design/icons";
import LoginChecked from "../Shared/LoginChecked";
import { NumberFormat } from "../Shared/Utility";

/** Prefer expense account (type 8), whether on debit or credit side. */
function getExpenseAccountSide(entry) {
  if (!entry) return null;
  const debitType = entry?.Debit_Account?.Account_Type_BE?.ID;
  const creditType = entry?.Credit_Account?.Account_Type_BE?.ID;
  if (debitType === 8) {
    return {
      id: entry.Debit_Account_ID,
      name: entry.Debit_Account?.Name || "Expense",
    };
  }
  if (creditType === 8) {
    return {
      id: entry.Credit_Account_ID,
      name: entry.Credit_Account?.Name || "Expense",
    };
  }
  return null;
}

function ExpensesReport() {
  const { RangePicker } = DatePicker;
  const [loading, setLoading] = useState(false);
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");
  const [EndDate, SetEndDate] = useState(
    dayjs().format("YYYY-MM-DD")
  );
  const [StartDate, SetStartDate] = useState(
    dayjs().subtract(1, "month").format("YYYY-MM-DD")
  );
  const [ReportType, setReportType] = useState("Account Wise");
  const [ExpenseData, setExpenseData] = useState({});
  const [ListOfRecords, setListOfRecords] = useState([]);
  const [DatewiseList, setDateWiseList] = useState([]);
  const [expenseAccountSums, setExpenseAccountSums] = useState([]);
  const [totalExpense, setTotalExpense] = useState(0);

  useEffect(() => {
    document.title = "Expenses Report";
  }, []);

  useEffect(() => {
    if (!UserID || !AccessKey) return;
    setLoading(true);
    const data = {
      UserID: UserID,
      AccessKey: AccessKey,
      StartDate: StartDate,
      EndDate: EndDate,
      ReportType: ReportType,
    };

    axios({
      method: "post",
      url: config.base_url + "Report/GetExpenseReport",
      headers: { "Content-Type": "application/json" },
      data: data,
    })
      .then(function (response) {
        if (response.data.status_code === 1) {
          setExpenseData(response.data);
          const vouchers = response.data.ListofVouchers || [];
          setListOfRecords(vouchers);
          setDateWiseList(response.data.ListofExpenseVouchers || []);
          setTotalExpense(response.data.TotalExpenses ?? 0);
        } else {
          setExpenseData({});
          setListOfRecords([]);
          setDateWiseList([]);
          setTotalExpense(0);
          message.error(
            response.data.status_message || "Could not load expenses report."
          );
        }
      })
      .catch(function () {
        message.error("Network error while loading expenses report.");
      })
      .finally(function () {
        setLoading(false);
      });
  }, [EndDate, StartDate, ReportType, UserID, AccessKey]);

  useEffect(() => {
    const sums = {};
    (ListOfRecords || []).forEach((entry) => {
      const side = getExpenseAccountSide(entry);
      if (!side) return;
      const amount = Number(entry.Amount) || 0;
      if (sums[side.id]) {
        sums[side.id].sum += amount;
      } else {
        sums[side.id] = { name: side.name, sum: amount };
      }
    });
    setExpenseAccountSums(Object.values(sums));
  }, [ListOfRecords]);

  const columns = [
    {
      title: "Dated",
      dataIndex: "Dated",
      key: "Dated",
      render: (_, record) =>
        dayjs(record.Dated).format(config.date_format),
    },
    {
      title: "Debit Account",
      dataIndex: "Debit_Account",
      key: "DebitAccount",
      render: (_, record) => record.Debit_Account?.Name,
    },
    {
      title: "Credit Account",
      dataIndex: "Credit_Account",
      key: "CreditAccount",
      render: (_, record) => record.Credit_Account?.Name,
    },
    {
      title: "Description",
      dataIndex: "Description",
      key: "Description",
    },
    {
      title: "Amount",
      dataIndex: "Amount",
      key: "Amount",
      align: "right",
      render: (_, record) => NumberFormat(record.Amount, 0),
    },
    {
      title: "Action",
      dataIndex: "Action",
      key: "Action",
      render: (_, record) => (
        <Tooltip title="Edit voucher">
          <NavLink to={`/vouchers/add-voucher/?ID=${record.ID}`}>
            <EditOutlined />
          </NavLink>
        </Tooltip>
      ),
    },
  ];

  const columns1 = [
    {
      title: "Sr.",
      dataIndex: "ID",
      key: "ID",
      render: (_, record, index) => index + 1,
    },
    {
      title: "Dated",
      dataIndex: "Dated",
      key: "Dated",
      render: (_, record) =>
        dayjs(record.Dated).format(config.date_format),
    },
    {
      title: "Amount",
      dataIndex: "Amount",
      key: "Amount",
      align: "right",
      render: (_, record) => NumberFormat(record.Amount, 0),
    },
  ];

  const getFooterContent = () => (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Row>
          <Col span={16}>
            <p>
              <b>Total Expense:</b>
            </p>
          </Col>
          <Col span={6} align="right">
            <p>
              <b>{NumberFormat(ExpenseData.TotalExpenses, 0)}</b>
            </p>
          </Col>
        </Row>
      </Col>
    </Row>
  );

  const onDateChange = (dates, value) => {
    if (!dates || !value || value.length < 2 || !value[0] || !value[1]) {
      return;
    }
    SetStartDate(value[0]);
    SetEndDate(value[1]);
  };

  const disableDate = (current) => {
    return current && current > moment().endOf("day");
  };

  return (
    <>
      <LoginChecked />
      <div className="right-side-contents">
        <div className="page-content">
          <div className="page-header">
            <h3 className="page-title">Expenses Report</h3>

            <div className="header-actions">
              <NavLink
                to={`/report-printexpensreport/print-list/?StartDate=${encodeURIComponent(
                  StartDate
                )}&EndDate=${encodeURIComponent(
                  EndDate
                )}&ReportType=${encodeURIComponent(ReportType)}`}
                target="_blank"
              >
                <Button type="dashed" size="large" shape="round">
                  <PrinterOutlined />
                  Print Report
                </Button>
              </NavLink>
            </div>
          </div>
          <div className="filters-wrap">
            <Form>
              <Form.Item>
                <RangePicker
                  value={[dayjs(StartDate), dayjs(EndDate)]}
                  onChange={onDateChange}
                  disabledDate={disableDate}
                  allowClear={false}
                />
              </Form.Item>
              <Form.Item>
                <Select
                  style={{ width: "160px" }}
                  placeholder="Report Type"
                  value={ReportType}
                  onChange={(value) => setReportType(value)}
                  options={[
                    { value: "Account Wise", label: "Account Wise" },
                    { value: "Date Wise", label: "Date Wise" },
                  ]}
                />
              </Form.Item>
            </Form>
          </div>

          {ReportType === "Date Wise" ? (
            <Table
              size="small"
              scroll={{ x: "100%" }}
              loading={loading}
              columns={columns1}
              dataSource={DatewiseList}
              rowKey={(r, i) => `d-${r.Dated}-${i}`}
              footer={getFooterContent}
              pagination={false}
              bordered
            />
          ) : (
            <Table
              size="small"
              scroll={{ x: "100%" }}
              loading={loading}
              columns={columns}
              dataSource={ListOfRecords}
              rowKey={(r) => r.ID}
              footer={getFooterContent}
              pagination={false}
              bordered
            />
          )}

          <div className="custom-table">
            <Row gutter={[20, 20]}>
              <Col md={12} xs={24}>
                <div className="fb-card">
                  <div className="fb-card-header">
                    <div className="fb-card-title">
                      <span>
                        <ArrowTrendingDownIcon />
                      </span>
                      <h4>Expenses Details</h4>
                    </div>
                  </div>

                  <div
                    className="fb-card-body table "
                    style={{ "--cardheight": "200px" }}
                  >
                    <ul className="profit-list">
                      {expenseAccountSums.map((item, index) => (
                        <li key={index}>
                          <h4>{item.name}</h4>
                          <p>{NumberFormat(item.sum, 0)}</p>
                        </li>
                      ))}

                      <li>
                        <h4 style={{ color: "#ec0d0d" }}>Total Expense</h4>
                        <p className="loss">
                          {NumberFormat(totalExpense, 0)}
                        </p>
                      </li>
                    </ul>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    </>
  );
}

export default ExpensesReport;
